import React, { useEffect, useState, useMemo, useRef } from 'react';

import { CustomButton } from '@components/Styles/ButtonStyle';
import { CustomStack } from '@components/Styles/CommonStyle';
import api from '@api/axios';
import { useDialog } from '@context/DialogContext';
import { useLoading } from '@context/LoadingContext';
import { useNavigate } from 'react-router-dom';
import ProjectCreateTableV2 from '@components/common/Table/ProjectCreateTableV2';
import Fab from "@mui/material/Fab";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const ProjectCreateV2 = () => {
  const { showLoading, hideLoading } = useLoading();
  const { showDialog } = useDialog();
  const navigate = useNavigate();

  const projectTableRef = useRef();
  const scrollRef = useRef(null);
  const customContainerRef = useRef(null);

  // 프로젝트 ID
  const id = new URLSearchParams(window.location.search).get('id') || null;
  // console.log('프로젝트ID : ' + id);

  const handleSave = () => {
    const submitInfo = getSubmitInfo();
    // console.log('submitInfo');
    // console.log(submitInfo);

    // 사업정보 입력 확인
    if (
      submitInfo.projectInfo.name === '' ||
      submitInfo.projectInfo.amount === '' ||
      submitInfo.projectInfo.orderingCompany === '' ||
      submitInfo.projectInfo.accountCompany === '' ||
      submitInfo.projectInfo.projectManager === '' ||
      submitInfo.projectInfo.startDate === '' ||
      submitInfo.projectInfo.endDate === ''
    ) {
      showDialog({ title: '확인', message: '사업정보를 입력해 주세요.' });
      return;
    }

    // 신규 시 신규 프로젝트 등록, 수정 시 수정
    let url = null;
    if (id) url = `/api/project/updateProject?id=${id}`;
    else url = '/api/project/createProject';

    const projectInfo = submitInfo.projectInfo;
    const startDate = submitInfo.projectInfo.startDate;
    const endDate = submitInfo.projectInfo.endDate;
    const monthlySale = submitInfo.monthlySale;
    const monthlyPurchase = submitInfo.monthlyPurchase;
    const monthlyExternalStaff = submitInfo.monthlyExternalStaff;
    const monthlyManagementFee = submitInfo.monthlyManagementFee;
    const projectStaffInfo = submitInfo.projectStaffInfo;

    // 인력투입현황 시작일 또는 종료일 정보 확인
    let projectStaffDateEmptyFlag = false
    projectStaffInfo.forEach((item) => {
      if (item.name !== '' && (item.startDate === '' || item.endDate === '')) {
        projectStaffDateEmptyFlag = true;
      }
    });

    if (projectStaffDateEmptyFlag) {
      showDialog({ title: '확인', message: '인력투입현황의 시작일 또는 종료일을 입력해 주세요.' });
      return;
    }

    showLoading();
    api
      .post(url, {
        projectInfo,
        startDate,
        endDate,
        monthlySale,
        monthlyPurchase,
        monthlyExternalStaff,
        monthlyManagementFee,
        projectStaffInfo,
      })
      .then((res) => {
        // console.log('res');
        // console.log(res);
        if (res.status === 201) {
          navigate('/project/projectList');
        }
      })
      .catch((err) => {
        if (err.response.status === 400) {
          showDialog({ title: '확인', message: err.response.data.message });
        }
        // console.log('err');
        // console.log(err);
      })
      .finally(() => {
        hideLoading();
      });
  };

  useEffect(() => {
    if (id) {
      // console.log('id');
      // console.log(id);
      api.get(`/api/project/readProject?id=${id}`).then((res) => {
        // console.log('res');
        // console.log(res);

        if (res.status === 200) {
          setProjectInfo(res.data.projectInfo);
          setSaleInfo(res.data.monthlySale);
          setPurchaseInfo(res.data.monthlyPurchase);
          setExternalStaffInfo(res.data.monthlyExternalStaff);
          setManagementFeeInfo(res.data.monthlyManagementFee);
          setStaffInfo(res.data.projectStaffInfo);
        }
      });
    }
  }, []);

  // 댓글 탭으로 이동하는 경우 댓글 섹션으로 스크롤
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');

    if (tabParam === 'comment' && id) {

      // DOM 렌더링 완료 후 댓글 섹션으로 스크롤
      setTimeout(() => {
        const commentSection = document.querySelector('[data-section="comment"]');
        if (commentSection) {
          commentSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, [id]);
  
  // 제출 정보 조회
  const getSubmitInfo = () => {
    if (projectTableRef.current) {
      return projectTableRef.current.getSubmitInfo();
    }
  };

  // 사업정보 설정
  const setProjectInfo = (info) => {
    if (projectTableRef.current) {
      projectTableRef.current.setProjectInfo(info);
    }
  };

  // 매출 설정
  const setSaleInfo = (info) => {
    if (projectTableRef.current) {
      projectTableRef.current.setSaleInfo(info);
    }
  };

  // 매입 설정
  const setPurchaseInfo = (info) => {
    if (projectTableRef.current) {
      projectTableRef.current.setPurchaseInfo(info);
    }
  };

  // 외부인력 설정
  const setExternalStaffInfo = (info) => {
    if (projectTableRef.current) {
      projectTableRef.current.setExternalStaffInfo(info);
    }
  };

  // 판관비 설정
  const setManagementFeeInfo = (info) => {
    if (projectTableRef.current) {
      projectTableRef.current.setManagementFeeInfo(info);
    }
  };

  // 인력투입현황 설정
  const setStaffInfo = (info) => {
    if (projectTableRef.current) {
      projectTableRef.current.setProjectStaffInfo(info);
    }
  };

  const scrollToTop = () => {
    const scrollableElement = document.querySelector('.Toolpad_DashboardLayout-main') || 
                             document.querySelector('main') || 
                             document.querySelector('.MuiBox-root[role="main"]');
    if (scrollableElement) {
      scrollableElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const scrollToBottom = () => {
    const scrollableElement = document.querySelector('.Toolpad_DashboardLayout-main') || 
                             document.querySelector('main') || 
                             document.querySelector('.MuiBox-root[role="main"]');
    if (scrollableElement) {
      scrollableElement.scrollTo({
        top: scrollableElement.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <React.Fragment>
      <ProjectCreateTableV2 id={id} ref={projectTableRef} />
      <CustomStack justifyContent="center" mt={4}>
        <CustomButton color="gray" onClick={() => navigate('/project/projectList')}>
          취소
        </CustomButton>
        {id ? (
          <CustomButton onClick={handleSave}>수정</CustomButton>
        ) : (
          <CustomButton onClick={handleSave}>저장</CustomButton>
        )}
      </CustomStack>
      <div
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          zIndex: 1000,
        }}
      >
        <Fab color="primary" onClick={scrollToTop} size="medium">
          <KeyboardArrowUpIcon />
        </Fab>
        <Fab color="primary" onClick={scrollToBottom} size="medium">
          <KeyboardArrowDownIcon />
        </Fab>
      </div>
    </React.Fragment>
  );
};

export default ProjectCreateV2;
